/**
 * Weather Impact Widget - Premium Liquid Crystal Design
 * ======================================================
 * Shows weather forecast and historical sales correlation
 * with staffing recommendations. Apple-inspired glassmorphism.
 */

import React from 'react';
import {
  Sun, Cloud, CloudRain, CloudSnow, Wind, Thermometer,
  TrendingUp, TrendingDown, Users, ChevronRight, AlertTriangle, Droplets
} from 'lucide-react';

// Industry-specific weather insights
const INDUSTRY_WEATHER_INSIGHTS = {
  hospitality: [
    { type: 'positive', text: 'Weekend forecast excellent - consider extending outdoor seating hours' },
    { type: 'warning', text: 'Wednesday rain expected - reduce prep by 15%' },
  ],
  retail: [
    { type: 'positive', text: 'Sunny weekend ahead - expect 20% higher foot traffic in-store' },
    { type: 'warning', text: 'Midweek rain - boost online promotions and email campaigns' },
  ],
  healthcare: [
    { type: 'positive', text: 'Cold snap approaching - expect increased flu/cold appointments next week' },
    { type: 'warning', text: 'Allergy season peak - ensure antihistamine stock is adequate' },
  ],
  property: [
    { type: 'positive', text: 'Clear Saturday forecast - ideal conditions for open homes and inspections' },
    { type: 'warning', text: 'Rain forecast Wednesday - reschedule outdoor property photos' },
  ],
  technology: [
    { type: 'positive', text: 'Storm warning may increase remote work - expect higher platform usage' },
    { type: 'warning', text: 'Power outage risk in Surry Hills - ensure cloud backups are current' },
  ],
  construction: [
    { type: 'positive', text: 'Clear skies through Friday - schedule all outdoor concrete pours' },
    { type: 'warning', text: 'Heavy rain Thursday - suspend excavation work on Site B' },
  ],
  fitness: [
    { type: 'positive', text: 'Rainy week ahead - expect 25% higher indoor class attendance' },
    { type: 'warning', text: 'Heatwave Saturday - increase A/C capacity and water station checks' },
  ],
  finance: [
    { type: 'positive', text: 'End of financial year approaching - increase client review meeting slots' },
    { type: 'warning', text: 'Market volatility expected - prepare client communication templates' },
  ],
};

const INDUSTRY_STAFF_RECS = {
  hospitality: '+2 FOH',
  retail: '+1 floor staff',
  healthcare: 'Normal staffing',
  property: 'Schedule open homes',
  technology: 'Normal operations',
  construction: 'Full crew outdoor',
  fitness: '+1 instructor',
  finance: 'Normal staffing',
};

const WeatherImpactWidget = ({
  industry = 'hospitality',
  className = '',
  compact = false,
  onViewDetails
}) => {
  const insights = INDUSTRY_WEATHER_INSIGHTS[industry] || INDUSTRY_WEATHER_INSIGHTS.hospitality;
  const staffRec = INDUSTRY_STAFF_RECS[industry] || '+2 staff';
  const WEATHER_FORECAST = {
    today: { temp: 24, high: 26, low: 18, condition: 'sunny', humidity: 45, wind: 12, salesImpact: '+12%', staffRec },
    week: [
      { day: 'Mon', temp: 24, condition: 'sunny', salesImpact: '+12%' },
      { day: 'Tue', temp: 22, condition: 'cloudy', salesImpact: '+5%' },
      { day: 'Wed', temp: 19, condition: 'rainy', salesImpact: '-8%' },
      { day: 'Thu', temp: 21, condition: 'cloudy', salesImpact: '+3%' },
      { day: 'Fri', temp: 25, condition: 'sunny', salesImpact: '+15%' },
      { day: 'Sat', temp: 27, condition: 'sunny', salesImpact: '+22%' },
      { day: 'Sun', temp: 26, condition: 'partly_cloudy', salesImpact: '+18%' },
    ],
    insights,
  };

  const weatherIcons = {
    sunny: { icon: Sun, gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-500/30', bg: 'bg-amber-50', text: 'text-amber-600' },
    cloudy: { icon: Cloud, gradient: 'from-gray-400 to-slate-500', shadow: 'shadow-gray-500/30', bg: 'bg-gray-50', text: 'text-gray-600' },
    partly_cloudy: { icon: Cloud, gradient: 'from-blue-400 to-sky-500', shadow: 'shadow-blue-500/30', bg: 'bg-blue-50', text: 'text-blue-600' },
    rainy: { icon: CloudRain, gradient: 'from-blue-500 to-indigo-600', shadow: 'shadow-blue-600/30', bg: 'bg-blue-50', text: 'text-blue-700' },
    snowy: { icon: CloudSnow, gradient: 'from-[#4DA6FF] to-blue-500', shadow: 'shadow-[#007AFF]/30', bg: 'bg-cyan-50', text: 'text-[#0066CC]' },
  };

  const { today, week, insights: weatherInsights } = WEATHER_FORECAST;
  const TodayIcon = weatherIcons[today.condition]?.icon || Sun;
  const todayStyle = weatherIcons[today.condition] || weatherIcons.sunny;

  if (compact) {
    return (
      <div 
        className={`relative overflow-hidden rounded-3xl bg-white/70 dark:bg-gray-900/70 backdrop-blur-2xl border border-white/30 dark:border-gray-700/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] dark:shadow-[0_8px_32px_rgb(0,0,0,0.3)] p-5 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] hover:-translate-y-0.5 ${className}`} 
        data-testid="weather-impact-compact"
      >
        {/* Glass overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent dark:from-white/5 pointer-events-none" />
        
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${todayStyle.gradient} flex items-center justify-center shadow-lg ${todayStyle.shadow}`}>
              <TodayIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">{today.temp}°C</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{today.condition.replace('_', ' ')}</p>
            </div>
          </div>
          <div className="text-right">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${today.salesImpact.startsWith('+') ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'} whitespace-nowrap`}>
              {today.salesImpact}
            </span>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Sales Impact</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-6 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] ${className}`} 
      data-testid="weather-impact-widget"
    >
      {/* Background gradient orb */}
      <div className="absolute -top-20 -right-20 w-56 h-56 bg-gradient-to-br from-amber-100/50 to-orange-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25">
              <Thermometer className="w-5 h-5 text-white" />
            </div>
            <div className="flex items-center gap-2.5">
              <h3 className="font-semibold text-gray-900 tracking-tight">Weather Impact</h3>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100 whitespace-nowrap">Live</span>
            </div>
          </div>
          <button 
            onClick={onViewDetails}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap"
          >
            Full Forecast <ChevronRight className="w-3 h-3 ml-1" />
          </button>
        </div>

        {/* Today's Weather - Hero Card */}
        <div className="p-4 rounded-2xl bg-gradient-to-br from-amber-50/80 to-orange-50/50 border border-amber-100 mb-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${todayStyle.gradient} flex items-center justify-center shadow-xl ${todayStyle.shadow}`}>
                <TodayIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-4xl font-bold text-gray-900 tracking-tight">{today.temp}°C</p>
                <p className="text-sm text-gray-600 capitalize">{today.condition.replace('_', ' ')}</p>
                <p className="text-xs text-gray-500">H: {today.high}° L: {today.low}°</p>
              </div>
            </div>
            <div className="text-right space-y-2">
              <div className="flex items-center gap-2 justify-end">
                {today.salesImpact.startsWith('+') ? (
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-500" />
                )}
                <span className={`text-2xl font-bold ${today.salesImpact.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}`}>
                  {today.salesImpact}
                </span>
              </div>
              <p className="text-xs text-gray-500">Sales Impact</p>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200 whitespace-nowrap">
                <Users className="w-3 h-3 mr-1" />
                {today.staffRec}
              </span>
            </div>
          </div>
        </div>

        {/* 7-Day Forecast */}
        <div className="mb-5">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">7-Day Outlook</h4>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
            {week.map((day, idx) => {
              const DayIcon = weatherIcons[day.condition]?.icon || Sun;
              const dayStyle = weatherIcons[day.condition] || weatherIcons.sunny;
              const isWeekend = idx >= 5;
              return (
                <div 
                  key={day.day} 
                  className={`text-center p-2.5 rounded-2xl border transition-all hover:shadow-md hover:-translate-y-0.5 ${
                    isWeekend 
                      ? 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200' 
                      : 'bg-gradient-to-br from-gray-50 to-white border-gray-100'
                  }`}
                >
                  <p className="text-[10px] font-semibold text-gray-500 uppercase">{day.day}</p>
                  <div className={`w-8 h-8 mx-auto my-1.5 rounded-xl bg-gradient-to-br ${dayStyle.gradient} flex items-center justify-center shadow-md ${dayStyle.shadow}`}>
                    <DayIcon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm font-bold text-gray-900">{day.temp}°</p>
                  <p className={`text-[10px] font-semibold ${day.salesImpact.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}`}>
                    {day.salesImpact}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Insights */}
        <div className="space-y-2">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Weather Insights</h4>
          {insights.map((insight, idx) => (
            <div 
              key={`${insight?.id ?? insight?.name ?? insight?.title ?? 'insight'}-${idx}`} 
              className={`flex items-start gap-3 p-3 rounded-2xl border transition-all hover:shadow-md ${
                insight.type === 'positive' 
                  ? 'bg-gradient-to-r from-emerald-50/80 to-blue-50/50 border-emerald-100' 
                  : 'bg-gradient-to-r from-amber-50/80 to-orange-50/50 border-amber-100'
              }`}
            >
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                insight.type === 'positive' 
                  ? 'bg-gradient-to-br from-emerald-400 to-[#0066CC] shadow-emerald-500/25' 
                  : 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/25'
              } shadow-lg`}>
                {insight.type === 'positive' ? (
                  <TrendingUp className="w-4 h-4 text-white" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-white" />
                )}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{insight.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeatherImpactWidget;
